import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AdminLayout } from "@/components/Layout";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [byOrg, setByOrg] = useState([]);
  const [targets, setTargets] = useState([]);
  const [filters, setFilters] = useState({ organization_id: "", location_id: "", activity_type_id: "", start: "", end: "" });
  const [locations, setLocations] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [orgSort, setOrgSort] = useState("conversations"); // "conversations" | "behind"

  const params = useMemo(() => Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)), [filters]);

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") {
      const t = iso(today); setFilters(f => ({ ...f, start: t, end: t }));
    } else if (preset === "week") {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7; // Mon=0
      const monday = new Date(d); monday.setDate(d.getDate() - day);
      setFilters(f => ({ ...f, start: iso(monday), end: iso(today) }));
    } else if (preset === "month") {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      setFilters(f => ({ ...f, start: iso(first), end: iso(today) }));
    } else if (preset === "quarter") {
      const q = Math.floor(today.getMonth() / 3);
      const first = new Date(today.getFullYear(), q * 3, 1);
      setFilters(f => ({ ...f, start: iso(first), end: iso(today) }));
    } else if (preset === "all") {
      setFilters(f => ({ ...f, start: "", end: "" }));
    }
  };

  useEffect(() => { api.get("/organizations").then(r => setOrgs(r.data));
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/activity-types").then(r => setActivityTypes(r.data));
    api.get("/targets").then(r => setTargets(r.data));
  }, []);

  useEffect(() => {
    api.get("/admin/stats/overview", { params }).then(r => setOverview(r.data));
    api.get("/admin/stats/by-organization", { params: { start: params.start, end: params.end } }).then(r => setByOrg(r.data));
  }, [params]);

  const national = targets.find(t => t.target_type === "national");

  const sortedByOrg = useMemo(() => {
    const list = [...byOrg];
    if (orgSort === "behind") {
      // Orgs WITH a target that are behind first, most-behind at top;
      // then orgs without a target; then those on-track / ahead at the bottom.
      const score = (o) => {
        if (o.target_pct === null || o.target_pct === undefined) return 100000; // no target -> bottom
        if (o.target_status === "ahead") return 90000 + (o.target_pct);
        if (o.target_status === "on_track") return 80000 + (o.target_pct);
        // behind: prefer largest gap between expected and current
        const gap = (o.target_expected || 0) - (o.target_current || 0);
        return -gap;
      };
      list.sort((a, b) => score(a) - score(b));
    } else {
      list.sort((a, b) => (b.conversations || 0) - (a.conversations || 0));
    }
    return list;
  }, [byOrg, orgSort]);

  return (
    <AdminLayout title="Kampanjöversikt" subtitle="Socialdemokraterna Skåne">
      <div className="flex flex-wrap gap-1 mb-4 bg-white border border-slate-200 rounded-full p-1 w-fit" data-testid="quick-date-pills">
        {[
          { key: "today", label: "Idag" },
          { key: "week", label: "Vecka" },
          { key: "month", label: "Månad" },
          { key: "quarter", label: "Kvartal" },
          { key: "all", label: "Allt" },
        ].map(p => (
          <button key={p.key} data-testid={`quick-${p.key}`} onClick={() => applyPreset(p.key)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors">
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <FilterSelect label="Arbetarekommun" value={filters.organization_id} onValue={v => setFilters(f => ({ ...f, organization_id: v }))} options={orgs} tid="filter-org"/>
        <FilterSelect label="Plats" value={filters.location_id} onValue={v => setFilters(f => ({ ...f, location_id: v }))} options={locations} tid="filter-loc"/>
        <FilterSelect label="Aktivitetstyp" value={filters.activity_type_id} onValue={v => setFilters(f => ({ ...f, activity_type_id: v }))} options={activityTypes} tid="filter-at"/>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="text-xs text-slate-500">Från</label><Input data-testid="filter-start" type="date" value={filters.start} onChange={e => setFilters(f => ({ ...f, start: e.target.value }))}/></div>
          <div><label className="text-xs text-slate-500">Till</label><Input data-testid="filter-end" type="date" value={filters.end} onChange={e => setFilters(f => ({ ...f, end: e.target.value }))}/></div>
        </div>
      </div>

      {!overview && <div className="text-slate-400">Laddar…</div>}
      {overview && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <BigCard label="Totalt samtal" value={overview.total_conversations} highlight tid="ov-total"/>
          <BigCard label="Idag" value={overview.today_conversations} tid="ov-today"/>
          <BigCard label="Denna vecka" value={overview.week_conversations} tid="ov-week"/>
          <BigCard label="Denna månad" value={overview.month_conversations} tid="ov-month"/>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <SmallCard label="Aktiviteter" value={overview.total_activities} tid="ov-act"/>
          <SmallCard label="Aktiva medlemmar" value={overview.active_members} tid="ov-active"/>
          <SmallCard label="Reg. medlemmar" value={overview.total_members} tid="ov-members"/>
          <SmallCard label="Snitt/aktivitet" value={overview.avg_conversations_per_activity} tid="ov-avg"/>
        </div>

        {national && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6" data-testid="admin-national-target">
            <div className="flex justify-between items-baseline">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Kampanjmål</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-1">{national.name}</div>
              </div>
              <div className="text-right"><div className="font-heading text-3xl font-extrabold number-tabular">{national.current?.toLocaleString("sv-SE")} <span className="text-slate-400 text-lg font-normal">/ {national.target_conversations.toLocaleString("sv-SE")}</span></div></div>
            </div>
            <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
              <div className="h-full bg-[#E1251B] transition-all" style={{ width: `${Math.min(100, (national.current/national.target_conversations)*100)}%` }}/>
            </div>
          </div>
        )}

        <div className="mt-8">
          <h2 className="font-heading text-xl font-extrabold text-slate-900 mb-3">Nyckelplatser</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {overview.featured_locations.map(l => (
              <button key={l.id} onClick={() => nav(`/admin/rapporter?location=${l.id}`)} data-testid={`feat-${l.name}`}
                className="text-left bg-slate-900 text-white rounded-2xl p-6 hover:bg-slate-800 transition-colors">
                <div className="text-xs uppercase tracking-widest opacity-60 font-semibold">{l.name}</div>
                <div className="font-heading text-4xl font-extrabold mt-2 number-tabular">{l.total.toLocaleString("sv-SE")}</div>
                <div className="text-sm opacity-70 mt-1">samtal totalt</div>
                <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/10 text-xs">
                  <div><div className="opacity-60">Idag</div><div className="font-bold number-tabular">{l.today}</div></div>
                  <div><div className="opacity-60">Vecka</div><div className="font-bold number-tabular">{l.week}</div></div>
                  <div><div className="opacity-60">Månad</div><div className="font-bold number-tabular">{l.month}</div></div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Samtal över tid (senaste 30 dagarna)</div>
          {overview.timeline.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer><LineChart data={overview.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false}/>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}/>
                <Line type="monotone" dataKey="conversations" stroke="#E1251B" strokeWidth={2.5} dot={{ r: 3 }}/>
              </LineChart></ResponsiveContainer>
            </div>
          ) : <div className="text-sm text-slate-400 py-10 text-center">Ingen data för valt intervall.</div>}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-extrabold text-slate-900">Arbetarekommuner</h2>
            <div className="flex items-center gap-2">
              <div className="hidden md:flex items-center gap-2 text-[11px] text-slate-500">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/> I mål</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"/> På rätt väg</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-500"/> Efter</span>
              </div>
              <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1">
                <button data-testid="org-sort-conversations" onClick={() => setOrgSort("conversations")}
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${orgSort === "conversations" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Samtal</button>
                <button data-testid="org-sort-behind" onClick={() => setOrgSort("behind")}
                  className={`text-xs px-3 py-1 rounded-full font-semibold ${orgSort === "behind" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Ligger efter</button>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="org-table">
              <TableHeader><TableRow>
                <TableHead>Arbetarekommun</TableHead>
                <TableHead className="text-right">Samtal</TableHead>
                <TableHead className="text-right">Aktiviteter</TableHead>
                <TableHead className="text-right">Aktiva medlemmar</TableHead>
                <TableHead className="w-[240px]">Kampanjmål</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sortedByOrg.map(o => (
                  <TableRow key={o.organization_id} data-testid={`org-row-${o.organization_name}`}
                    onClick={() => nav(`/admin/kommun/${o.organization_id}`)}
                    className="cursor-pointer hover:bg-slate-50 transition-colors">
                    <TableCell className="font-medium">{o.organization_name}</TableCell>
                    <TableCell className="text-right font-heading font-bold number-tabular">{o.conversations.toLocaleString("sv-SE")}</TableCell>
                    <TableCell className="text-right number-tabular">{o.activities}</TableCell>
                    <TableCell className="text-right number-tabular">{o.active_members} / {o.total_members}</TableCell>
                    <TableCell><TargetCell row={o}/></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </>)}
    </AdminLayout>
  );
}

function BigCard({ label, value, highlight, tid }) {
  return (
    <div className={`rounded-2xl p-6 ${highlight ? "bg-[#E1251B] text-white" : "bg-white border border-slate-200"}`} data-testid={tid}>
      <div className={`text-[11px] uppercase tracking-widest font-semibold ${highlight ? "text-white/70" : "text-slate-500"}`}>{label}</div>
      <div className="font-heading text-4xl font-extrabold mt-1 number-tabular">{value?.toLocaleString("sv-SE")}</div>
    </div>
  );
}
function SmallCard({ label, value, tid }) {
  return (
    <div className="rounded-xl p-4 bg-white border border-slate-200" data-testid={tid}>
      <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1 number-tabular">{typeof value === "number" ? value.toLocaleString("sv-SE") : value}</div>
    </div>
  );
}
function FilterSelect({ label, value, onValue, options, tid }) {
  return (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <Select value={value || "all"} onValueChange={v => onValue(v === "all" ? "" : v)}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Alla"/></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alla</SelectItem>
          {options.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function TargetCell({ row }) {
  if (row.target_conversations == null) {
    return <span className="text-xs text-slate-400">Inget mål</span>;
  }
  const pct = Math.min(100, Math.max(0, row.target_pct || 0));
  const status = row.target_status; // "ahead" | "on_track" | "behind" | "not_started"
  const barColor = status === "ahead" ? "bg-emerald-500"
    : status === "on_track" ? "bg-amber-500"
    : status === "behind" ? "bg-rose-500"
    : "bg-slate-300";
  const label = status === "ahead" ? "I mål"
    : status === "on_track" ? "På rätt väg"
    : status === "behind" ? "Ligger efter"
    : "Ej startat";
  const chipColor = status === "ahead" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : status === "on_track" ? "text-amber-700 bg-amber-50 border-amber-200"
    : status === "behind" ? "text-rose-700 bg-rose-50 border-rose-200"
    : "text-slate-500 bg-slate-50 border-slate-200";
  const expectedPct = row.target_conversations ? Math.min(100, Math.round(((row.target_expected || 0) / row.target_conversations) * 100)) : 0;
  return (
    <div className="min-w-[220px]" data-testid={`org-target-${row.organization_name}`}>
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className={`px-1.5 py-0.5 rounded-full border font-semibold ${chipColor}`} data-testid={`org-target-status-${row.organization_name}`}>{label}</span>
        <span className="font-heading font-bold text-slate-900 number-tabular">{Math.round(pct)}%</span>
      </div>
      <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}/>
        {expectedPct > 0 && expectedPct < 100 && (
          <div className="absolute top-0 bottom-0 border-l border-slate-400" style={{ left: `${expectedPct}%` }} title={`Förväntad takt ${expectedPct}%`}/>
        )}
      </div>
      <div className="text-[10px] text-slate-500 mt-1 number-tabular">
        {(row.target_current || 0).toLocaleString("sv-SE")} / {row.target_conversations.toLocaleString("sv-SE")}
      </div>
    </div>
  );
}
